import React from "react";
import { FlexFormContainer, SeparatorErrorMessage } from "../separator/SpacerPart.styles";
import { Checkbox, FormContainer, Tab, TabsContainer } from "../image/ImagePart.styles";
import { isNil } from "ramda";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  BackgroundImageFieldSet,
  BackgroundOptionsContainer,
  CheckboxContainer,
  ClickableBackgroundDropdown,
  ContentContainer,
  DocColourInput,
  DocSelectorFieldSet,
  DocumentAddButton,
  ImportURLContainer,
  ListContainer,
  PDFObjectContainer,
  PDFViewerContainer,
  SupportedFilesMessage,
} from "./DocumentPart.styles";
import { IState } from "../../../../../App/reducer";
import { connect } from "react-redux";
import { AxiosInterceptedResult } from "../../../../../api/interceptors";
import { IFileUploadResponseData } from "../../../../../api/CourseAuthorApi";
import {
  DocBorderThicknessPreset,
  DocMarginPreset,
  IDocumentPart,
  IDocumentSettings,
} from "../../../../../components/course-author/types";
import { CourseInstructionPartOperations } from "../CourseInstructionPart.types";
import { DividerColorFieldSet, DividerFormSelect } from "../separator/DividerPart.styles";
import { config } from "../../../../../config";
import { PDFObject } from "react-pdfobject";
import { AxiosResponse } from "axios";
import { LinkToImageIcon, UploadBgImageIcon } from "../../../../../components/utils/Icons/Part/Document";
import { DEFAULT_DOCUMENT_SETTINGS } from "../../../../../components/course-author/helpers/patches/creators";
import DropDown, { DropdownOption } from "../../../../../components/utils/Dropdown";
import { BackgroundImageModal, LinkToImageContent, UploadBackgroundImageContent } from "./BackgroundImageModals";
import { PostUploadComponent } from "./PostUploadComponent";
import { DropZoneComponent, renderDropZone, renderLinkInput } from "./Dropzone";
import { MAX_FILE_SIZE } from "../../../../../utils/index";
import { CheckboxDescription } from "../../../../../App/Common/Components/FormComponents";

const hasInvalidFiles = (files: File[] | undefined): boolean =>
  isNil(files) || files.length !== 1 || files[0].type !== "application/pdf";

const isInvalidURL = (url: string): boolean => url.length === 0 || url.indexOf(".pdf") === -1;

export interface IDocumentPartProps {
  accountId: string;
  documentPart: IDocumentPart;
  edit: boolean;
  partOperations: CourseInstructionPartOperations;
  uploadFile: (
    file: File,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData[]>>;
  uploadFileViaUrl: (
    link: string,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData>>;
}

const DocumentPart: React.FC<IDocumentPartProps> = props => {
  const { uploadFile, uploadFileViaUrl, edit, documentPart, partOperations } = props;

  const tabRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = React.useState<string>("File Upload");
  const [documentFromURL, setDocumentFromURL] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [settings, setSettings] = React.useState<IDocumentSettings["settings"]>(documentPart.content.settings);
  const [uploadProgress, setUploadProgress] = React.useState<number>(settings.fileDocumentId ? 100 : 0);

  const mkDocumentURL = (docId: string): string => `${config.saltcomplianceApiRoot}/resources/${docId}/_download`;

  const updateSettings = (newSettings: IDocumentSettings["settings"]) => {
    setSettings(newSettings);
    partOperations.editDocument({ settings: newSettings });
  };

  const onChangeDocumentFromURL = (e: React.ChangeEvent<HTMLInputElement>) => setDocumentFromURL(e.target.value);

  const handleTabClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setErrorMessage("");
    updateSettings({
      ...settings,
      fileDocumentId: "",
    });
    setUploadProgress(0);
    const name = e.currentTarget.innerText;
    setActiveTab(name);
  };

  const onUploadProgress = (progressEvent: ProgressEvent) => {
    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
    setUploadProgress(progress);
  };

  const handleDocumentUpload = (files: File[] | undefined) => {
    setErrorMessage("");
    if (hasInvalidFiles(files)) {
      return alert(`Make sure to upload only one document\nFile type must .pdf `);
    }
    if (files![0].size > MAX_FILE_SIZE) {
      return setErrorMessage("File Exceeds Maximum Size Limit");
    }
    uploadFile(files![0], onUploadProgress).then(
      ([error, response]: [string | null, AxiosResponse<IFileUploadResponseData[], any> | null]) => {
        if (error) {
          return setErrorMessage("No response");
        }
        updateSettings({
          ...settings,
          fileDocumentId: response!.data[0].mediaId.slice(4),
          fileName: files![0].name,
          fileSize: files![0].size,
        });
      }
    );
  };

  const handleDocumentUploadViaURL = () => {
    if (isInvalidURL(documentFromURL)) {
      return alert("Please enter a valid URL");
    }
    uploadFileViaUrl(documentFromURL, onUploadProgress).then(
      ([error, response]: [string | null, AxiosResponse<IFileUploadResponseData, any> | null]) => {
        if (error) {
          return setErrorMessage("No response");
        } else if (response) {
          updateSettings({
            ...settings,
            fileDocumentId: response.data.mediaId.slice(4),
            fileName: response.data.name,
          });
        }
      }
    );
  };

  React.useEffect(() => {
    setSettings(documentPart.content.settings);
  }, [documentPart.content.settings.fileDocumentId]);

  if (!edit) {
    return settings.fileDocumentId ? (
      <PDFViewer url={mkDocumentURL(settings.fileDocumentId)} settings={settings} />
    ) : null;
  }

  const onCancelUpload = () => {
    setUploadProgress(0);
    updateSettings({
      ...DEFAULT_DOCUMENT_SETTINGS,
    });
  };

  return (
    <ContentContainer>
      <SeparatorErrorMessage>{errorMessage}</SeparatorErrorMessage>
      <FlexFormContainer>
        <FormContainer>
          <TabsContainer>
            <Tab ref={tabRef} onClick={handleTabClick} active={activeTab === "File Upload"}>
              File Upload
            </Tab>
            <Tab ref={tabRef} onClick={handleTabClick} active={activeTab === "Link"} data-testid="link-tab">
              Link
            </Tab>
          </TabsContainer>
          {activeTab === "File Upload" ? (
            <>
              {!settings.fileDocumentId && uploadProgress !== 100 ? (
                renderDropZone(handleDocumentUpload, <DocumentDropZone />, "pdf-uploader")
              ) : errorMessage.length === 0 ? (
                <PostUploadComponent
                  uploadProgress={uploadProgress}
                  url={mkDocumentURL(settings.fileDocumentId)}
                  fileName={settings.fileName}
                  fileSize={settings.fileSize}
                  onCancelUpload={onCancelUpload}
                />
              ) : (
                renderDropZone(handleDocumentUpload, <DocumentDropZone />, "pdf-uploader")
              )}
            </>
          ) : (
            <>
              {!settings.fileDocumentId ? (
                <ImportURLContainer>
                  {renderLinkInput(inputRef, documentFromURL, onChangeDocumentFromURL)}
                  <DocumentAddButton onClick={handleDocumentUploadViaURL} data-testid="upload-url-button">
                    <span>
                      <FaPlus /> Add
                    </span>
                  </DocumentAddButton>
                </ImportURLContainer>
              ) : errorMessage.length === 0 ? (
                <PostUploadComponent
                  uploadProgress={uploadProgress}
                  url={mkDocumentURL(settings.fileDocumentId)}
                  fileName={settings.fileName}
                  fileSize={settings.fileSize}
                  onCancelUpload={onCancelUpload}
                />
              ) : (
                renderLinkInput(inputRef, documentFromURL, onChangeDocumentFromURL)
              )}

              <SupportedFilesMessage>Supported file types: PDF</SupportedFilesMessage>
            </>
          )}
        </FormContainer>
      </FlexFormContainer>
      {settings.fileDocumentId && (
        <>
          <DocumentSettings
            settings={settings}
            updateSettings={updateSettings}
            setErrorMessage={setErrorMessage}
            uploadFile={uploadFile}
            uploadFileViaUrl={uploadFileViaUrl}
          />
          <PDFViewer url={mkDocumentURL(settings.fileDocumentId)} settings={settings} />
        </>
      )}
    </ContentContainer>
  );
};

const DocumentDropZone = () => <DropZoneComponent supportedFilesTypes={["PDF"]} supportedFilesMbSize={10} />;

interface IDocumentSettingsProps {
  settings: IDocumentSettings["settings"];
  updateSettings: (settings: IDocumentSettings["settings"]) => void;
  setErrorMessage: (message: string) => void;
  uploadFile: (
    file: File,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData[]>>;
  uploadFileViaUrl: (
    link: string,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData>>;
}

const DocumentSettings: React.FC<IDocumentSettingsProps> = props => {
  const { settings, updateSettings, setErrorMessage, uploadFile, uploadFileViaUrl } = props;

  const [isUploadImgModalOpen, setIsUploadImgModalOpen] = React.useState(false);
  const [isLinkToImageModalOpen, setIsLinkToImageModalOpen] = React.useState(false);
  const [uploadBgImgProgress, setUploadBgImgProgress] = React.useState<number>(settings.backgroundImageId ? 100 : 0);
  const [bgImageErrorMessage, setBgImageErrorMessage] = React.useState<string>("");

  const closeUploadImgModal = () => setIsUploadImgModalOpen(false);
  const closeLinkToImageModal = () => setIsLinkToImageModalOpen(false);

  const onUploadBgImgProgress = (progressEvent: ProgressEvent) => {
    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
    setUploadBgImgProgress(progress);
  };

  const onPropertiesChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === "") {
      setErrorMessage("Please fill in all fields");
      return;
    } else if (Number(e.target.value) < 0) {
      setErrorMessage("Values cannot be negative");
      return;
    }

    setErrorMessage("");

    if (["style", "size", "borderStyle", "borderColor", "backgroundColor"].includes(e.target.name)) {
      updateSettings({
        ...settings,
        [e.target.name.replace("documentSettings.", "")]: e.target.value,
      });
    } else if (["margin", "borderThickness"].includes(e.target.name)) {
      updateSettings({
        ...settings,
        [e.target.name.replace("documentSettings.", "")]: Number(e.target.value),
      });
    } else if (["hasBackground"].includes(e.target.name)) {
      updateSettings({
        ...settings,
        backgroundColor: settings.backgroundColor,
        backgroundImageId: settings.backgroundImageId,
        hasBackground: (e as React.ChangeEvent<HTMLInputElement>).target.checked,
      });
    }
  };

  const onCancelBgImageUpload = () => {
    setUploadBgImgProgress(0);
    updateSettings({
      ...settings,
      backgroundImageId: "",
      backgroundImageName: "",
      backgroundImageSize: 0,
    });
  };

  const imageOptions: DropdownOption[] = [
    { value: "uploadImg", label: "Upload Image", icon: <UploadBgImageIcon /> },
    { value: "uploadImgViaUrl", label: "Link to Image", icon: <LinkToImageIcon /> },
    settings.backgroundImageId && { value: "removeImg", label: "Remove Image", icon: <FaTrash /> },
  ].filter(Boolean) as DropdownOption[];

  const handleSelectImageOption = (value: string) => {
    setBgImageErrorMessage("");

    !settings.backgroundImageId && setUploadBgImgProgress(0);

    if (value === "removeImg") {
      onCancelBgImageUpload();
    } else if (value === "uploadImg") {
      setIsUploadImgModalOpen(true);
      setIsLinkToImageModalOpen(false);
    } else {
      setIsLinkToImageModalOpen(true);
      setIsUploadImgModalOpen(false);
    }
  };

  return (
    <>
      <FlexFormContainer data-testid="document-settings">
        <ListContainer>
          <DocSelectorFieldSet>
            <legend>Style</legend>
            <DividerFormSelect name="style" id="style" value={settings.style} onChange={onPropertiesChange}>
              <option value="border">Border</option>
              <option value="no-border">No Border</option>
            </DividerFormSelect>
          </DocSelectorFieldSet>
          {settings.style === "border" && (
            <>
              <DocSelectorFieldSet>
                <legend>Type</legend>
                <DividerFormSelect
                  name="borderStyle"
                  id="borderStyle"
                  value={settings.borderStyle}
                  onChange={onPropertiesChange}
                >
                  <option value="solid">Line</option>
                  <option value="dotted">Dots</option>
                  <option value="dashed">Dashes</option>
                </DividerFormSelect>
              </DocSelectorFieldSet>
              <DocSelectorFieldSet>
                <legend>Thickness</legend>
                <DividerFormSelect
                  name="borderThickness"
                  id="borderThickness"
                  value={settings.borderThickness}
                  onChange={onPropertiesChange}
                >
                  <option value={DocBorderThicknessPreset.Small}>Small</option>
                  <option value={DocBorderThicknessPreset.Medium}>Medium</option>
                  <option value={DocBorderThicknessPreset.Large}>Large</option>
                </DividerFormSelect>
              </DocSelectorFieldSet>
              <DividerColorFieldSet>
                <legend>Colour</legend>
                <DocColourInput
                  name="borderColor"
                  placeholder="Colour"
                  type="color"
                  defaultValue={settings.borderColor}
                  onChange={onPropertiesChange}
                />
              </DividerColorFieldSet>
            </>
          )}
        </ListContainer>
        <DocSelectorFieldSet>
          <legend>Margin</legend>
          <DividerFormSelect name="margin" id="margin" value={settings.margin} onChange={onPropertiesChange}>
            <option value={DocMarginPreset.Small}>Small</option>
            <option value={DocMarginPreset.Medium}>Medium</option>
            <option value={DocMarginPreset.Large}>Large</option>
          </DividerFormSelect>
        </DocSelectorFieldSet>
        <DocSelectorFieldSet>
          <legend>Style</legend>
          <DividerFormSelect name="size" id="size" value={settings.size} onChange={onPropertiesChange}>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="full-width">Full width</option>
          </DividerFormSelect>
        </DocSelectorFieldSet>
        {settings.size !== "full-width" && (
          <ListContainer>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                name="hasBackground"
                onChange={onPropertiesChange}
                defaultChecked={settings.hasBackground}
              />
              <CheckboxDescription>Background</CheckboxDescription>
            </CheckboxContainer>
            {settings.hasBackground && (
              <BackgroundOptionsContainer>
                <DividerColorFieldSet>
                  <legend>Colour</legend>
                  <DocColourInput
                    name="backgroundColor"
                    placeholder="Colour"
                    type="color"
                    defaultValue={settings.backgroundColor}
                    onChange={onPropertiesChange}
                  />
                </DividerColorFieldSet>
                <BackgroundImageFieldSet>
                  <legend>Image</legend>
                  <DropDown
                    options={imageOptions}
                    onSelect={handleSelectImageOption}
                    toggleComponent={
                      <ClickableBackgroundDropdown settings={settings}>
                        {!settings.backgroundImageId && "Add Image"}
                      </ClickableBackgroundDropdown>
                    }
                  />
                </BackgroundImageFieldSet>
              </BackgroundOptionsContainer>
            )}
          </ListContainer>
        )}
      </FlexFormContainer>

      {isUploadImgModalOpen && (
        <BackgroundImageModal
          bgImageErrorMessage={bgImageErrorMessage}
          closeModal={closeUploadImgModal}
          onCancelBgImageUpload={onCancelBgImageUpload}
          settings={settings}
          showModal={isUploadImgModalOpen}
          uploadBgImgProgress={uploadBgImgProgress}
          modalTitle="Upload Background Image"
        >
          <UploadBackgroundImageContent
            onUploadBgImgProgress={onUploadBgImgProgress}
            setBgImageErrorMessage={setBgImageErrorMessage}
            settings={settings}
            updateSettings={updateSettings}
            uploadFile={uploadFile}
            closeModal={closeUploadImgModal}
          />
        </BackgroundImageModal>
      )}
      {isLinkToImageModalOpen && (
        <BackgroundImageModal
          bgImageErrorMessage={bgImageErrorMessage}
          closeModal={closeLinkToImageModal}
          onCancelBgImageUpload={onCancelBgImageUpload}
          settings={settings}
          showModal={isLinkToImageModalOpen}
          uploadBgImgProgress={uploadBgImgProgress}
          modalTitle="Link to Image"
        >
          <LinkToImageContent
            settings={settings}
            updateSettings={updateSettings}
            uploadFileViaUrl={uploadFileViaUrl}
            setBgImageErrorMessage={setBgImageErrorMessage}
            onUploadBgImgProgress={onUploadBgImgProgress}
            closeModal={closeUploadImgModal}
          />
        </BackgroundImageModal>
      )}
    </>
  );
};

interface IPDFViewerProps {
  url: string;
  settings: IDocumentSettings["settings"];
}

const PDFViewer: React.FC<IPDFViewerProps> = props => {
  const { url, settings } = props;

  const containerId = `pdf-viewer-${settings.fileDocumentId}`;

  return (
    <PDFViewerContainer settings={settings}>
      <PDFObjectContainer id={containerId} settings={settings} data-testid={containerId}>
        <PDFObject
          url={url}
          containerId={containerId}
          pdfOpenParams={{
            page: 1,
            view: "FitH",
            zoom: "scale",
          }}
        />
      </PDFObjectContainer>
    </PDFViewerContainer>
  );
};

const mapStateToProps = (state: IState) => ({
  accountId: state.App._.user?.accountId,
  uploadFile: state.App.CourseAuthor._.fileUploadApi.uploadFile,
  uploadFileViaUrl: state.App.CourseAuthor._.fileUploadApi.uploadFileViaUrl,
});

export default connect(mapStateToProps)(DocumentPart);
